import React, { useState, useEffect, type ReactNode } from 'react'

import type { Project } from '../../../../types/Project'
import type { WorkId } from '../../../../types/WorkId'
import type { ErrorStates } from '../../../../types/ErrorStates'
import type { ParsedParameters } from './Parameters/parseParameters'
import { setCustomAttribute } from '../../../../utils/new-relic'
import { useProjectLoader } from '../ProjectContext/useProjectLoader'
import { getCountryFromLocale } from '../../../../utils/LocalizationHelpers'

export interface IAppContext {
  initialWorkIds: WorkId[]
  primaryProject?: Project
  culture: string
  country: string
  editSource?: string
  isCareAgent: boolean
  nextStepUrl: string
  isAppStateInitialized: boolean
  isInError: boolean
  errorState?: ErrorStates
  setErrorState: (errorState: ErrorStates) => void
}

export const AppContext = React.createContext<IAppContext>({} as IAppContext)

const AppContextProvider: React.FC<{
  queryParameters: ParsedParameters
  locale: string
  children: ReactNode
}> = ({ queryParameters, locale, children }) => {
  // track the overall page status
  const [isAppStateInitialized, setIsAppStateInitialized] = useState<boolean>(false)
  const [isInError, setIsInError] = useState<boolean>(false)
  const [errorState, setErrorState] = useState<ErrorStates>(null)
  const [primaryProject, setPrimaryProject] = useState<Project>()
  const [nextStepUrl, setNextStepUrl] = useState<string>('/c')

  // workEntityIds is the initial IDs when the page is first loaded.
  const { workEntityIds, isCareAgent, editSource } = queryParameters

  const country = getCountryFromLocale(locale)

  const { projects: loaderProjects, isSuccess: loaderIsSuccess, isError: loaderIsError } = useProjectLoader(locale, workEntityIds[0] ?? '')

  useEffect(() => {
    if (loaderIsError) {
      setErrorState('UnableToLoadProjects')
      return
    }
    if (!loaderIsSuccess || !loaderProjects) {
      return
    }
    setPrimaryProject(loaderProjects[0])
  }, [loaderProjects, loaderIsSuccess, loaderIsError])

  useEffect(() => {
    if (!primaryProject) {
      return
    }
    setNextStepUrl(primaryProject.nextStepUrl)
    // TODO : track multiple products (but for now just track the first one)
    setCustomAttribute('productKey', primaryProject.productKey)
    setIsAppStateInitialized(true)
  }, [primaryProject])

  useEffect(() => {
    setIsInError(errorState !== null)
  }, [errorState])

  return (
    <AppContext.Provider
      value={{
        initialWorkIds: workEntityIds,
        primaryProject,
        culture: locale,
        country,
        editSource,
        isCareAgent,
        nextStepUrl,
        isAppStateInitialized,
        isInError,
        errorState,
        setErrorState,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export default AppContextProvider
